import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import axios from 'axios';
import { getApiUrl } from '../services/api';
import PhaseTimeline, { PHASES, PHASE_COLOR } from '../components/opportunities/PhaseTimeline';
import { Target, MapPin, ArrowLeft, Save, Loader } from 'lucide-react';

const PASTA_FIELDS = [
  { key: 'parking',    label: 'Parking',     desc: 'Disponibilidad de aparcamiento' },
  { key: 'access',     label: 'Acceso',      desc: 'Facilidad de acceso al punto' },
  { key: 'signing',    label: 'Señalización', desc: 'Visibilidad de rótulos y señales' },
  { key: 'traffic',    label: 'Tráfico',     desc: 'Volumen de tráfico peatonal/rodado' },
  { key: 'activity',   label: 'Actividad',   desc: 'Actividad comercial del entorno' },
  { key: 'visibility', label: 'Visibilidad', desc: 'Visibilidad del local desde la vía' },
];

const NewOpportunityPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Two modes:
  //  - coords mode (from map dbl-click): lat/lng present → reverse geocode for the name.
  //  - address mode (from "Nueva"): no coords → geocode the address (catastro) on blur.
  const hasCoords = searchParams.has('lat') && searchParams.has('lng');
  const initialLat = hasCoords ? parseFloat(searchParams.get('lat')) : null;
  const initialLng = hasCoords ? parseFloat(searchParams.get('lng')) : null;

  const [coords, setCoords] = useState(
    hasCoords && !isNaN(initialLat) && !isNaN(initialLng) ? { lat: initialLat, lng: initialLng } : null
  );
  const [cadastralRef, setCadastralRef] = useState('');   // 14-char parcela
  const [cadastralFull, setCadastralFull] = useState('');  // 20-char inmueble (address mode)

  const [form, setForm] = useState({
    name: '',
    address: '',
    phase: 'exploration',
    notes: '',
    pastaScores: { parking: '', access: '', signing: '', traffic: '', activity: '', visibility: '' },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [geoStatus, setGeoStatus] = useState('');
  const lastGeocoded = useRef('');

  // Coords mode: reverse geocode via DeepNexus to fill the name + address.
  useEffect(() => {
    if (!coords) return;
    let cancelled = false;
    setGeocoding(true);
    axios.get(`${getApiUrl()}/location/direction`, {
      params: { lat: coords.lat, lon: coords.lng },
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(({ data }) => {
        if (cancelled) return;
        const postal = data?.direccion?.postal || '';
        const rc = (data?.codigosCatastrales || [])[0] || '';
        setForm(prev => ({
          ...prev,
          name: prev.name || postal,
          address: prev.address || postal,
        }));
        if (rc) setCadastralRef(rc);
      })
      .catch(() => { /* keep empty; user can type */ })
      .finally(() => { if (!cancelled) setGeocoding(false); });
    return () => { cancelled = true; };
    // Only on mount / when coords first set from the URL.
  }, []);

  // Address mode: geocode the typed address (catastro) when leaving the field.
  const handleAddressBlur = async () => {
    if (hasCoords) return; // coords mode doesn't geocode from address
    const q = form.address.trim();
    if (!q || q === lastGeocoded.current || geocoding) return;
    lastGeocoded.current = q;
    setGeocoding(true);
    setGeoStatus('Calculando ubicación...');
    try {
      const { data } = await axios.post(
        `${getApiUrl()}/location/geocode-catastro`,
        { address: q },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCoords({ lat: data.latitude, lng: data.longitude });
      setCadastralRef(data.referenciaCatastral14 || '');
      setCadastralFull(data.referenciaCatastral || '');
      if (data.formattedAddress) setForm(prev => ({ ...prev, address: data.formattedAddress }));
      setGeoStatus(
        `Ubicación calculada${data.referenciaCatastral14 ? ` · Ref. catastral: ${data.referenciaCatastral14}` : ''}`
      );
    } catch (err) {
      setCoords(null);
      setCadastralRef('');
      setCadastralFull('');
      setGeoStatus(err.response?.data?.message || 'No se pudo calcular la ubicación (se podrá crear sin GPS).');
    } finally {
      setGeocoding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('El nombre es obligatorio.'); return; }
    setSaving(true);
    setError('');

    // Build pasta scores (only include numeric values)
    const pastaScores = {};
    PASTA_FIELDS.forEach(f => {
      const v = parseInt(form.pastaScores[f.key]);
      if (v >= 1 && v <= 10) pastaScores[f.key] = v;
    });

    // Cadastre metadata (store 14-char reference; keep 20-char in notes).
    const cadastre = {};
    if (cadastralRef) cadastre.reference = cadastralRef;
    if (cadastralFull && cadastralFull !== cadastralRef) cadastre.notes = `Referencia catastral (inmueble): ${cadastralFull}`;

    try {
      const { data } = await axios.post(
        `${getApiUrl()}/opportunities`,
        {
          name: form.name.trim(),
          ...(coords ? { coordinates: [coords.lng, coords.lat] } : {}),
          address: form.address,
          phase: form.phase,
          metadata: {
            notes: form.notes,
            ...(Object.keys(cadastre).length ? { cadastre } : {}),
          },
          pastaScores: Object.keys(pastaScores).length ? pastaScores : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/opportunities/${data._id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear la oportunidad');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    color: '#F0F0F0',
    padding: '10px 14px',
    fontSize: '0.9rem',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.7rem',
    color: '#E27B58',
    textTransform: 'uppercase',
    fontWeight: '700',
    letterSpacing: '1px',
    marginBottom: '6px',
  };

  return (
    <div style={{
      flex: 1,
      overflowY: 'auto',
      background: '#05070A',
      minHeight: '100%',
      display: 'flex',
      justifyContent: 'center',
      padding: '32px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 600 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/opportunities')}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#888', cursor: 'pointer', padding: '8px', display: 'flex' }}
          >
            <ArrowLeft size={16} />
          </button>
          <Target size={20} color="#E27B58" />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '900', color: '#E27B58', textTransform: 'uppercase', letterSpacing: '2px' }}>
              Nueva Oportunidad
            </h1>
            <div style={{ fontSize: '0.7rem', color: '#444', fontFamily: 'monospace', marginTop: '2px' }}>
              {coords ? `${coords.lat.toFixed(6)}°N · ${coords.lng.toFixed(6)}°E` : 'Sin ubicación'}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>

          {/* Coordinates badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 14px',
            background: coords ? 'rgba(226,123,88,0.08)' : 'rgba(255,255,255,0.03)',
            border: `1px solid ${coords ? 'rgba(226,123,88,0.3)' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: '0.82rem',
            color: coords ? '#E27B58' : '#666',
            fontFamily: 'monospace',
          }}>
            <MapPin size={14} />
            {geocoding
              ? 'Calculando ubicación...'
              : coords
                ? `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`
                : 'Sin ubicación — se calculará desde la dirección'}
          </div>

          {/* Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Nombre del punto *</label>
            <input
              style={inputStyle}
              placeholder="Ej: Local C/ Gran Vía 45"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              required
              onFocus={e => { e.target.style.borderColor = '#E27B58'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
            />
          </div>

          {/* Address */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>
              Dirección {geocoding && <span style={{ color: '#555', fontSize: '0.65rem', fontWeight: '400' }}>geocodificando...</span>}
            </label>
            <input
              style={inputStyle}
              placeholder={hasCoords ? 'Dirección del punto' : 'Escribe la dirección y sal del campo para calcular la ubicación'}
              value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })}
              onFocus={e => { e.target.style.borderColor = '#E27B58'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; handleAddressBlur(); }}
            />
            {!hasCoords && geoStatus && (
              <div style={{ marginTop: '6px', fontSize: '0.7rem', color: coords ? '#27AE60' : '#E27B58' }}>
                {geoStatus}
              </div>
            )}
          </div>

          {/* Phase */}
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Fase inicial</label>
            <PhaseTimeline
              currentPhase={form.phase}
              onSelectPhase={(phase) => setForm({ ...form, phase })}
            />
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Notas iniciales</label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
              placeholder="Observaciones, razones del interés..."
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              onFocus={e => { e.target.style.borderColor = '#E27B58'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
            />
          </div>

          {/* PASTA V scores (optional) */}
          <div style={{ marginBottom: '28px' }}>
            <label style={{ ...labelStyle, marginBottom: '12px' }}>
              PASTA V Scores
              <span style={{ color: '#444', fontWeight: '400', marginLeft: '8px', fontSize: '0.65rem' }}>opcional, 1-10</span>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
              {PASTA_FIELDS.map(f => (
                <div key={f.key}>
                  <label style={{ ...labelStyle, color: '#666', fontSize: '0.65rem' }}>{f.label}</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    style={{ ...inputStyle, fontSize: '0.85rem' }}
                    placeholder="1-10"
                    value={form.pastaScores[f.key]}
                    onChange={e => setForm({ ...form, pastaScores: { ...form.pastaScores, [f.key]: e.target.value } })}
                    onFocus={e => { e.target.style.borderColor = '#00CEC9'; }}
                    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{ color: '#E74C3C', fontSize: '0.82rem', background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              padding: '14px',
              background: saving ? 'rgba(226,123,88,0.5)' : '#E27B58',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: '900',
              fontSize: '0.95rem',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              transition: 'all 0.2s',
            }}
          >
            {saving ? <Loader size={16} className="spin" /> : <Save size={16} />}
            {saving ? 'Guardando...' : 'Crear Oportunidad'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewOpportunityPage;
